import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";
import JurisdictionSelect, { TurkiyeNotice } from "../components/JurisdictionSelect";
import {
    Send,
    ArrowUpRight,
    Plus,
    Sparkles,
    Trash2,
    Upload,
    History,
    FileText,
    BookOpen,
    Lock,
    PanelLeft,
    PanelRight,
    Loader2,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";

const EXAMPLES = [
    "What is the lawful basis of legitimate interests under GDPR Article 6(1)(f)?",
    "Which AI systems are classified as high-risk under the AI Act, and what conformity assessment applies?",
    "Summarise the ICT third-party risk obligations under DORA Chapter V.",
    "What did the CJEU hold in Schrems II about standard contractual clauses, and what does it mean for transfers to the US today?",
];

const DEFAULT_MODE = "general";
const DEFAULT_DEPTH = "standard";

export default function ResearchAssistant() {
    // chat
    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [question, setQuestion] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const scrollerRef = useRef(null);

    // controls
    const [mode, setMode] = useState(DEFAULT_MODE);
    const [depth, setDepth] = useState(DEFAULT_DEPTH);
    const [jurisdiction, setJurisdiction] = useState("eu");
    const [modesMeta, setModesMeta] = useState({ modes: [], depths: [] });

    // sidebars (mobile drawer state; desktop panels are always visible)
    const [mobileLeftOpen, setMobileLeftOpen] = useState(false);
    const [mobileRightOpen, setMobileRightOpen] = useState(false);

    // conversations + docs + usage
    const [conversations, setConversations] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [docLimits, setDocLimits] = useState(null);
    const [selectedDocIds, setSelectedDocIds] = useState([]);
    const [usage, setUsage] = useState(null);
    const [uploading, setUploading] = useState(false);

    // load metadata once
    useEffect(() => {
        api.get("/research/modes").then(({ data }) => setModesMeta(data)).catch(() => {});
        refreshConversations();
        refreshDocuments();
        refreshUsage();
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, loading]);

    const refreshConversations = async () => {
        try {
            const { data } = await api.get("/conversations");
            setConversations(data.items || []);
        } catch (_e) { /* non-blocking */ }
    };
    const refreshDocuments = async () => {
        try {
            const { data } = await api.get("/documents");
            setDocuments(data.items || []);
            setDocLimits(data.limits || null);
        } catch (_e) { /* non-blocking */ }
    };
    const refreshUsage = async () => {
        try {
            const { data } = await api.get("/usage");
            setUsage(data);
        } catch (_e) { /* non-blocking */ }
    };

    const startNew = () => {
        setConversationId(null);
        setMessages([]);
        setError("");
        setQuestion("");
        setSelectedDocIds([]);
        setMode(DEFAULT_MODE);
        setDepth(DEFAULT_DEPTH);
        setJurisdiction("eu");
    };

    const openConversation = async (id) => {
        try {
            const { data } = await api.get(`/conversations/${id}`);
            setConversationId(id);
            setMessages(data.messages || []);
            setMode(data.mode || DEFAULT_MODE);
            setDepth(data.depth || DEFAULT_DEPTH);
            const lastJ = [...(data.messages || [])].reverse().find((m) => m.jurisdiction);
            setJurisdiction(lastJ?.jurisdiction || "eu");
            setSelectedDocIds(data.document_ids || []);
            setError("");
        } catch (_e) {
            setError("Could not open that conversation.");
        }
    };

    const deleteConversation = async (id, e) => {
        e?.stopPropagation?.();
        if (!confirm("Delete this conversation?")) return;
        try {
            await api.delete(`/conversations/${id}`);
            setConversations((cs) => cs.filter((c) => c.id !== id));
            if (conversationId === id) startNew();
        } catch (_e) { /* non-blocking */ }
    };

    const askWithText = useCallback(
        async (text) => {
            const q = (text ?? question).trim();
            if (!q || loading) return;
            setError("");
            setMessages((m) => [...m, { role: "user", content: q, mode, depth }]);
            setQuestion("");
            setLoading(true);
            try {
                const { data } = await api.post("/ask", {
                    question: q,
                    conversation_id: conversationId,
                    mode,
                    depth,
                    jurisdiction,
                    document_ids: selectedDocIds,
                    suggest_followups: true,
                });
                let result = data;
                if (data.status === "pending" && data.job_id) {
                    // Türkiye-layer answers run with live web search as an
                    // async job; poll until it completes (up to ~6 min).
                    result = null;
                    for (let i = 0; i < 150; i++) {
                        await new Promise((r) => setTimeout(r, 2500));
                        const { data: job } = await api.get(`/ask/jobs/${data.job_id}`);
                        if (job.status === "ready") {
                            result = { ...data, ...job };
                            break;
                        }
                        if (job.status === "failed") {
                            throw new Error(job.error || "Research failed. Your credits were refunded.");
                        }
                    }
                    if (!result) {
                        throw new Error("Timed out waiting for the answer. It may still complete; check the conversation shortly.");
                    }
                }
                setMessages((m) => [
                    ...m,
                    {
                        role: "assistant",
                        content: result.answer,
                        sources: result.sources,
                        follow_ups: result.follow_ups,
                        mode: result.mode,
                        depth: result.depth,
                    },
                ]);
                setConversationId(result.conversation_id);
                if (result.usage) setUsage(result.usage);
                refreshUsage();
                refreshConversations();
            } catch (err) {
                const detail = err?.response?.data?.detail;
                if (err?.response?.status === 402 && typeof detail === "object") {
                    setError(detail.message || "Monthly credit limit reached.");
                } else if (typeof detail === "string") {
                    setError(detail);
                } else if (err?.message && !err?.response) {
                    setError(err.message);
                } else {
                    setError("Something went wrong. Try again.");
                }
            } finally {
                setLoading(false);
            }
        },
        [question, loading, conversationId, mode, depth, jurisdiction, selectedDocIds],
    );

    const uploadFile = async (file) => {
        if (!file) return;
        setError("");
        setUploading(true);
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post("/documents", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDocuments((ds) => [data, ...ds]);
            // Auto-select on successful index
            if (data.status === "ready") {
                setSelectedDocIds((ids) =>
                    ids.includes(data.id) ? ids : [...ids, data.id],
                );
            }
            refreshDocuments();
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const toggleDoc = (id) => {
        setSelectedDocIds((ids) =>
            ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
        );
    };
    const removeDoc = async (id) => {
        if (!confirm("Delete this document and its index?")) return;
        try {
            await api.delete(`/documents/${id}`);
            setDocuments((ds) => ds.filter((d) => d.id !== id));
            setSelectedDocIds((ids) => ids.filter((x) => x !== id));
        } catch (_e) { /* non-blocking */ }
    };

    const verifiedSources = useMemo(() => {
        const last = [...messages].reverse().find((m) => m.role === "assistant");
        return last?.sources || [];
    }, [messages]);

    return (
        <div className="flex h-screen bg-[#F5F0E8]" data-testid="research-shell">
            {/* LEFT: Conversation history */}
            <ConversationsPanel
                mobileOpen={mobileLeftOpen}
                onCloseMobile={() => setMobileLeftOpen(false)}
                conversations={conversations}
                activeId={conversationId}
                onPick={openConversation}
                onDelete={deleteConversation}
                onNew={startNew}
            />

            {/* CENTER: Chat */}
            <section className="flex-1 min-w-0 flex flex-col">
                <TopBar
                    mode={mode}
                    setMode={setMode}
                    depth={depth}
                    setDepth={setDepth}
                    jurisdiction={jurisdiction}
                    setJurisdiction={setJurisdiction}
                    modes={modesMeta.modes}
                    depths={modesMeta.depths}
                    usage={usage}
                    onToggleLeft={() => setMobileLeftOpen(true)}
                    onToggleRight={() => setMobileRightOpen(true)}
                />

                <div
                    ref={scrollerRef}
                    className="flex-1 overflow-y-auto"
                    data-testid="research-scroller"
                >
                    <div className="container-edit py-8 max-w-4xl">
                        {messages.length === 0 && (
                            <EmptyState
                                onPick={(q) => askWithText(q)}
                                disabled={loading}
                            />
                        )}
                        <div className="space-y-6">
                            {messages.map((m, i) => (
                                <Bubble
                                    key={i}
                                    message={m}
                                    index={i}
                                    onFollowUp={(q) => askWithText(q)}
                                />
                            ))}
                            {loading && (
                                <div
                                    className="flex items-center gap-3 text-[#0D1B3E]/70"
                                    data-testid="research-loading"
                                >
                                    <Loader2 className="animate-spin" size={18} />
                                    <span className="text-sm">
                                        {jurisdiction === "eu"
                                            ? "Researching primary sources…"
                                            : "Verifying official Turkish sources via live search; this can take a couple of minutes…"}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <TurkiyeNotice jurisdiction={jurisdiction} className="container-edit max-w-4xl pb-2" />
                <Composer
                    question={question}
                    setQuestion={setQuestion}
                    onSubmit={() => askWithText()}
                    loading={loading}
                    error={error}
                    selectedDocs={selectedDocIds.length}
                    mode={mode}
                    depth={depth}
                />
            </section>

            {/* RIGHT: Documents + Sources */}
            <RightPanel
                mobileOpen={mobileRightOpen}
                onCloseMobile={() => setMobileRightOpen(false)}
                documents={documents}
                docLimits={docLimits}
                selectedDocIds={selectedDocIds}
                toggleDoc={toggleDoc}
                removeDoc={removeDoc}
                uploading={uploading}
                onUpload={uploadFile}
                sources={verifiedSources}
            />
        </div>
    );
}

// ---------- subcomponents -----------------------------------------------

function TopBar({
    mode,
    setMode,
    depth,
    setDepth,
    jurisdiction,
    setJurisdiction,
    modes,
    depths,
    usage,
    onToggleLeft,
    onToggleRight,
}) {
    return (
        <header className="border-b border-[#0D1B3E]/10 bg-[#F5F0E8]">
            <div className="container-edit pt-5 pb-3">
                <div className="flex items-end justify-between gap-4 flex-wrap pr-14 lg:pr-24">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                            <div className="label-eyebrow">Module</div>
                            <h1 className="font-display text-2xl lg:text-3xl mt-1 tracking-tight truncate">
                                Research Assistant
                            </h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        {usage && (
                            <div className="text-right" data-testid="research-usage">
                                <div className="label-eyebrow">This month</div>
                                <div className="font-display text-lg mt-0.5 whitespace-nowrap">
                                    {usage.used} / {usage.limit}
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#0D1B3E]/55 ml-2">
                                        credits
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile drawer entry-points: two clearly labelled, visually
                    separated controls: History (left drawer) vs Documents (right
                    drawer). Desktop hides these because both panels are visible
                    at all times. */}
                <div className="mt-4 flex gap-3 lg:hidden">
                    <button
                        type="button"
                        onClick={onToggleLeft}
                        className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 border border-[#0D1B3E]/25 bg-white text-sm hover:border-[#C9A84C] hover:text-[#C9A84C]"
                        data-testid="research-toggle-left"
                    >
                        <History size={16} /> Conversation history
                    </button>
                    <button
                        type="button"
                        onClick={onToggleRight}
                        className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 border border-[#C9A84C] bg-[#C9A84C]/10 text-sm text-[#0D1B3E] hover:bg-[#C9A84C]/20"
                        data-testid="research-toggle-right"
                    >
                        <Upload size={16} /> Upload PDF / DOCX
                    </button>
                </div>
                <div
                    className="mt-4 -mx-1 px-1 overflow-x-auto lg:overflow-x-visible flex items-center gap-3 flex-nowrap lg:flex-wrap"
                    data-testid="research-mode-bar"
                    style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "thin" }}
                >
                    <PillSelect
                        label="Mode"
                        value={mode}
                        onChange={setMode}
                        options={modes.map((m) => ({ value: m.key, label: m.name, hint: m.blurb }))}
                        testId="research-mode"
                    />
                    <PillSelect
                        label="Depth"
                        value={depth}
                        onChange={setDepth}
                        options={depths.map((d) => ({ value: d.key, label: d.name, hint: d.blurb }))}
                        testId="research-depth"
                    />
                    <JurisdictionSelect
                        value={jurisdiction}
                        onChange={setJurisdiction}
                        testId="research-jurisdiction"
                    />
                </div>
            </div>
        </header>
    );
}

function PillSelect({ label, value, onChange, options, testId }) {
    return (
        <div className="flex items-center gap-2 shrink-0" data-testid={`${testId}-group`}>
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/60">
                {label}
            </span>
            <div className="inline-flex border border-[#0D1B3E]/20 bg-white shrink-0">
                {options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        title={o.hint}
                        data-testid={`${testId}-option-${o.value}`}
                        className={`min-h-[44px] px-3 text-xs font-semibold tracking-wide transition-colors whitespace-nowrap ${
                            value === o.value
                                ? "bg-[#0D1B3E] text-[#F5F0E8]"
                                : "text-[#0D1B3E]/80 hover:bg-[#0D1B3E]/5"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function ConversationsPanelHeader({ onNew, onClose }) {
    return (
        <div className="p-4 border-b border-[#0D1B3E]/10 flex items-center justify-between">
            <span className="label-eyebrow">Conversations</span>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onNew}
                    data-testid="research-new-conversation"
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[#0D1B3E] hover:text-[#C9A84C]"
                >
                    <Plus size={14} /> New
                </button>
                {onClose && (
                    <button onClick={onClose} className="lg:hidden text-xs text-[#0D1B3E]/60">
                        Close
                    </button>
                )}
            </div>
        </div>
    );
}

function ConvList({ list, activeId, onPick, onDelete }) {
    if (list.length === 0) {
        return (
            <div className="p-6 text-sm text-[#0D1B3E]/60">
                No conversations yet. Ask a question to start one.
            </div>
        );
    }
    return (
        <ul className="flex-1 overflow-y-auto" data-testid="research-conversation-list">
            {list.map((c) => {
                const active = c.id === activeId;
                return (
                    <li key={c.id}>
                        {/* Row is a div with onClick so the delete <button> can nest. */}
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => onPick(c.id)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onPick(c.id);
                                }
                            }}
                            data-testid={`research-conversation-${c.id}`}
                            className={`cursor-pointer w-full text-left px-4 py-3 border-l-2 group ${
                                active
                                    ? "border-[#C9A84C] bg-white"
                                    : "border-transparent hover:bg-white"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="text-sm font-medium leading-snug line-clamp-2 text-[#0D1B3E]">
                                    {c.title || "Untitled"}
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => onDelete(c.id, e)}
                                    className="opacity-0 group-hover:opacity-100 text-[#0D1B3E]/40 hover:text-red-600"
                                    data-testid={`research-conversation-delete-${c.id}`}
                                    aria-label="Delete conversation"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/50">
                                {c.mode} · {c.depth} · {c.message_count} msg
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function ConversationsPanel({ mobileOpen, onCloseMobile, conversations, activeId, onPick, onDelete, onNew }) {
    return (
        <>
            {/* Desktop: always visible at lg+ */}
            <aside
                className="hidden lg:flex flex-col w-64 shrink-0 border-r border-[#0D1B3E]/10 bg-[#FAF6EF]"
                data-testid="research-conversations-panel"
            >
                <ConversationsPanelHeader onNew={onNew} />
                <ConvList list={conversations} activeId={activeId} onPick={onPick} onDelete={onDelete} />
            </aside>
            {/* Mobile drawer */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-black/40"
                    onClick={onCloseMobile}
                    data-testid="research-conversations-mobile-overlay"
                >
                    <aside
                        className="absolute left-0 top-0 bottom-0 w-72 bg-[#FAF6EF] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="research-conversations-mobile-drawer"
                    >
                        <ConversationsPanelHeader onNew={onNew} onClose={onCloseMobile} />
                        <ConvList
                            list={conversations}
                            activeId={activeId}
                            onPick={(id) => {
                                onPick(id);
                                onCloseMobile();
                            }}
                            onDelete={onDelete}
                        />
                    </aside>
                </div>
            )}
        </>
    );
}

function RightPanel({
    mobileOpen,
    onCloseMobile,
    documents,
    docLimits,
    selectedDocIds,
    toggleDoc,
    removeDoc,
    uploading,
    onUpload,
    sources,
}) {
    const fileInputRef = useRef(null);
    const allowed = !!docLimits?.allow_uploads;
    const body = (
        <RightPanelBody
            fileInputRef={fileInputRef}
            allowed={allowed}
            uploading={uploading}
            onUpload={onUpload}
            docLimits={docLimits}
            documents={documents}
            selectedDocIds={selectedDocIds}
            toggleDoc={toggleDoc}
            removeDoc={removeDoc}
            sources={sources}
        />
    );
    return (
        <>
            <aside
                className="hidden lg:flex flex-col w-80 shrink-0 border-l border-[#0D1B3E]/10 bg-[#FAF6EF]"
                data-testid="research-right-panel"
            >
                {body}
            </aside>
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-black/40"
                    onClick={onCloseMobile}
                    data-testid="research-right-mobile-overlay"
                >
                    <aside
                        className="absolute right-0 top-0 bottom-0 w-80 max-w-[88vw] bg-[#FAF6EF] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="research-right-mobile-drawer"
                    >
                        <div className="p-4 border-b border-[#0D1B3E]/10 flex items-center justify-between">
                            <span className="label-eyebrow">Workspace</span>
                            <button
                                type="button"
                                onClick={onCloseMobile}
                                className="text-xs text-[#0D1B3E]/60"
                            >
                                Close
                            </button>
                        </div>
                        {body}
                    </aside>
                </div>
            )}
        </>
    );
}

function RightPanelBody({
    fileInputRef,
    allowed,
    uploading,
    onUpload,
    docLimits,
    documents,
    selectedDocIds,
    toggleDoc,
    removeDoc,
    sources,
}) {
    return (
        <>
            {/* DOCUMENTS */}
            <div className="p-5 border-b border-[#0D1B3E]/10">
                <div className="flex items-center justify-between">
                    <span className="label-eyebrow">Documents</span>
                    {allowed ? (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="research-upload-button"
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#0D1B3E] hover:text-[#C9A84C] disabled:opacity-50"
                        >
                            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            Upload
                        </button>
                    ) : (
                        <Link
                            to="/app/pricing"
                            data-testid="research-upgrade-uploads"
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#C9A84C] hover:underline"
                        >
                            <Lock size={12} /> Upgrade
                        </Link>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onUpload(f);
                        e.target.value = "";
                    }}
                    data-testid="research-upload-input"
                />
                <p className="mt-2 text-[11px] text-[#0D1B3E]/60 leading-relaxed">
                    PDF or DOCX, up to {docLimits ? Math.floor(docLimits.max_file_bytes / (1024 * 1024)) : 12} MB.
                    Your documents and embeddings stay private to your account and are
                    never used to train any model.
                </p>

                {!allowed && (
                    <div className="mt-3 text-xs text-[#0D1B3E]/65 flex items-start gap-2">
                        <Lock size={12} className="mt-0.5 shrink-0" />
                        <span>
                            Document upload is included in the Professional and Team plans.
                        </span>
                    </div>
                )}

                <ul className="mt-4 space-y-2 max-h-64 overflow-y-auto" data-testid="research-document-list">
                    {documents.length === 0 ? (
                        <li className="text-xs text-[#0D1B3E]/55">No documents uploaded yet.</li>
                    ) : (
                        documents.map((d) => {
                            const ready = d.status === "ready";
                            const failed = d.status === "failed";
                            const selected = selectedDocIds.includes(d.id);
                            return (
                                <li
                                    key={d.id}
                                    className={`p-2.5 border ${selected ? "border-[#C9A84C] bg-white" : "border-[#0D1B3E]/15 bg-white/60"}`}
                                    data-testid={`research-document-${d.id}`}
                                >
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={() => toggleDoc(d.id)}
                                            disabled={!ready}
                                            data-testid={`research-document-toggle-${d.id}`}
                                            className="mt-1 accent-[#C9A84C]"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <FileText size={13} className="shrink-0 text-[#0D1B3E]/70" />
                                                <span className="text-xs font-medium truncate">{d.filename}</span>
                                            </div>
                                            <div className="mt-0.5 text-[10px] uppercase tracking-[0.16em] text-[#0D1B3E]/55 flex items-center gap-1.5">
                                                {ready && (
                                                    <>
                                                        <CheckCircle2 size={11} className="text-[#C9A84C]" />
                                                        {d.chunk_count} chunks
                                                    </>
                                                )}
                                                {failed && (
                                                    <>
                                                        <AlertTriangle size={11} className="text-red-600" />
                                                        Indexing failed
                                                    </>
                                                )}
                                                {d.status === "pending" && <span>Indexing…</span>}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                removeDoc(d.id);
                                            }}
                                            data-testid={`research-document-delete-${d.id}`}
                                            className="text-[#0D1B3E]/40 hover:text-red-600 shrink-0"
                                            aria-label="Delete document"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </label>
                                </li>
                            );
                        })
                    )}
                </ul>
            </div>

            {/* VERIFIED SOURCES */}
            <div className="p-5 flex-1 overflow-y-auto">
                <div className="flex items-center justify-between">
                    <span className="label-eyebrow">Verified Sources</span>
                    {sources.length > 0 && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#0D1B3E]/55">
                            {sources.length}
                        </span>
                    )}
                </div>
                {sources.length === 0 ? (
                    <p className="mt-3 text-xs text-[#0D1B3E]/55">
                        Sources cited in the last answer will appear here.
                    </p>
                ) : (
                    <ul className="mt-3 space-y-3" data-testid="research-sources-list">
                        {sources.map((s, i) => (
                            <li
                                key={i}
                                className="text-xs bg-white border border-[#0D1B3E]/15 p-3"
                                data-testid={`research-source-${i}`}
                            >
                                <div className="flex items-center gap-1.5">
                                    {s.kind === "document" ? (
                                        <FileText size={12} className="text-[#C9A84C]" />
                                    ) : (
                                        <BookOpen size={12} className="text-[#C9A84C]" />
                                    )}
                                    <span className="font-medium text-[#0D1B3E] line-clamp-2">
                                        {s.title}
                                    </span>
                                </div>
                                {s.citation && (
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[#0D1B3E]/55">
                                        {s.citation}
                                    </div>
                                )}
                                {s.section && (
                                    <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[#0D1B3E]/55">
                                        {s.section}
                                    </div>
                                )}
                                {s.excerpt && (
                                    <p className="mt-2 text-[11px] leading-snug text-[#0D1B3E]/75 italic line-clamp-4">
                                        “{s.excerpt}”
                                    </p>
                                )}
                                {s.url && (
                                    <a
                                        href={s.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#0D1B3E] hover:text-[#C9A84C]"
                                        data-testid={`research-source-link-${i}`}
                                    >
                                        Verify in official source <ArrowUpRight size={11} />
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    );
}

function EmptyState({ onPick, disabled }) {
    return (
        <div className="card-flat p-7 flex items-start gap-5">
            <EuStars size={48} />
            <div>
                <div className="label-eyebrow">Ask in plain English</div>
                <h2 className="font-display text-2xl mt-2 tracking-tight">
                    Pick a starting point.
                </h2>
                <p className="mt-2 text-sm text-[#0D1B3E]/70">
                    Switch mode for case analysis, compliance, legislative history or
                    comparative work. Attach a document on the right to ground the
                    answer in your own text.
                </p>
                <ul className="mt-5 space-y-2">
                    {EXAMPLES.map((ex) => (
                        <li key={ex}>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() => onPick(ex)}
                                className="text-left text-sm text-[#0D1B3E] hover:text-[#C9A84C] underline-offset-4 hover:underline disabled:opacity-50"
                                data-testid={`research-example-${ex.slice(0, 20).replace(/\s+/g, "-")}`}
                            >
                                {ex}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

function Composer({ question, setQuestion, onSubmit, loading, error, selectedDocs, mode, depth }) {
    return (
        <footer className="border-t border-[#0D1B3E]/10 bg-[#F5F0E8]">
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSubmit();
                }}
                className="container-edit py-4 max-w-4xl"
                data-testid="research-form"
            >
                {error && (
                    <div
                        className="mb-3 text-sm border border-red-500/40 bg-red-50 text-red-900 px-3 py-2 flex items-center justify-between gap-3"
                        data-testid="research-error"
                    >
                        <span>{error}</span>
                        <Link to="/app/pricing" className="underline whitespace-nowrap">
                            Upgrade →
                        </Link>
                    </div>
                )}
                <div className="flex items-end gap-3">
                    <textarea
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                onSubmit();
                            }
                        }}
                        rows={2}
                        placeholder="Ask about GDPR, the AI Act, CJEU case law…"
                        className="field flex-1 resize-none"
                        data-testid="research-input-question"
                        disabled={loading}
                    />
                    <button
                        type="submit"
                        disabled={loading || !question.trim()}
                        className="btn-primary disabled:opacity-50"
                        data-testid="research-submit-button"
                    >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        Ask
                    </button>
                </div>
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2 text-[10px] uppercase tracking-[0.2em] text-[#0D1B3E]/55">
                    <span>
                        {mode} · {depth}
                        {selectedDocs > 0 && (
                            <span className="text-[#C9A84C]">
                                {" "}· {selectedDocs} document{selectedDocs > 1 ? "s" : ""} attached
                            </span>
                        )}
                    </span>
                    <span>Enter to send · Shift+Enter for new line</span>
                </div>
            </form>
        </footer>
    );
}

function Bubble({ message, index, onFollowUp }) {
    if (message.role === "user") {
        return (
            <div className="flex justify-end" data-testid={`research-message-user-${index}`}>
                <div className="max-w-[80%] bg-[#0D1B3E] text-[#F5F0E8] px-5 py-3.5 leading-relaxed whitespace-pre-wrap">
                    {message.content}
                </div>
            </div>
        );
    }
    return (
        <div className="flex" data-testid={`research-message-assistant-${index}`}>
            <div className="max-w-[88%] bg-white border border-[#0D1B3E]/15 px-6 py-5 w-full">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <EuStars size={20} />
                        <span className="label-eyebrow">EuroLaw Hub</span>
                    </div>
                    {message.mode && (
                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                            {message.mode} · {message.depth}
                        </span>
                    )}
                </div>
                <div
                    className="whitespace-pre-wrap text-[#0D1B3E] leading-relaxed"
                    data-testid={`research-answer-${index}`}
                >
                    {message.content}
                </div>

                {message.follow_ups && message.follow_ups.length > 0 && (
                    <div className="mt-6">
                        <div className="label-eyebrow mb-2">Follow-up questions</div>
                        <div className="flex flex-wrap gap-2" data-testid={`research-followups-${index}`}>
                            {message.follow_ups.map((q, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => onFollowUp(q)}
                                    data-testid={`research-followup-${index}-${i}`}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#0D1B3E]/20 hover:border-[#C9A84C] hover:text-[#C9A84C] transition-colors"
                                >
                                    <Sparkles size={12} />
                                    {q}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {message.sources && message.sources.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-[#0D1B3E]/10">
                        <div className="label-eyebrow mb-2">Sources</div>
                        <ul className="space-y-1.5">
                            {message.sources.map((s, i) => (
                                <li key={i} className="text-sm flex items-start gap-1.5">
                                    {s.kind === "document" ? (
                                        <FileText size={12} className="text-[#C9A84C] mt-1 shrink-0" />
                                    ) : (
                                        <BookOpen size={12} className="text-[#C9A84C] mt-1 shrink-0" />
                                    )}
                                    {s.url ? (
                                        <a
                                            href={s.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[#0D1B3E] hover:text-[#C9A84C] inline-flex items-center gap-1"
                                            data-testid={`research-source-link-${index}-${i}`}
                                        >
                                            {s.title}
                                            {s.citation && (
                                                <span className="text-[#0D1B3E]/55">
                                                    {" · "}{s.citation}
                                                </span>
                                            )}
                                            <ArrowUpRight size={11} />
                                        </a>
                                    ) : (
                                        <span>
                                            {s.title}
                                            {s.section && (
                                                <span className="text-[#0D1B3E]/55">
                                                    {" · "}{s.section}
                                                </span>
                                            )}
                                            {s.citation && (
                                                <span className="text-[#0D1B3E]/55">
                                                    {" · "}{s.citation}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
